import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import { injectStripe } from 'react-stripe-elements';

import { Box, Button, Text, ShadowBox } from '@components/common';
import DefaultLoader from '@components/loading/DefaultLoader';
import PaymentSelection from '../PaymentSelection';

import CouponField from '../CouponField';
import { renderServiceItem } from '../commonRenders';
import ScheduledCheckoutItem from './ScheduledCheckoutItem';

import { AppSelectors } from '@redux/AppRedux';
import AuthActions, { AuthSelectors } from '@redux/AuthRedux';
import ScheduleActions, { ScheduleSelectors } from '@redux/ScheduleRedux';
import BookingActions, { BookingSelectors } from '@redux/BookingRedux';
import CleansActions from '@redux/CleansRedux';

import {
  transformSchedule,
  transfromPropertyToAddress
} from '@services/transform';
import { trackRecurSrvsBooked } from '@services/analytics';
import { BOOKING_STAGE, SURCHARGE_TEXT } from '@services/minte';
import { load as loadPixel } from '@services/pixel';

import API from '@utils/api';
import logger from '@utils/logger';
import { StripeConfig } from '@utils/config';
import { checkIfRescheduleError, getErrors } from '@utils/error';

import validationSchema from './schema';

class RecurringCheckoutForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      costs: [],
      isCalculating: true,
      costPayload: {}
    };
  }

  async componentDidMount() {
    const {
      scheduledItems,
      currentPackage,
      cycleLength,
      isPMUser,
      recipient,
      property
    } = this.props;
    const { propertyID } = property;

    try {
      const costPayload = {
        package: currentPackage,
        cycle: scheduledItems,
        cycleLength,
        isPMUser,
        recipient,
        propertyID
      };
      const pp = await API.getRecurringCostCalculations(costPayload);
      this.setState({ costs: pp.result, isCalculating: false, costPayload });
    } catch (err) {
      this.setState({ isCalculating: false });
    }
  }

  handleCostUpdate = newCost => {
    this.setState({ costs: newCost });
  };

  renderScheduledItem = (
    scheduleItem,
    itemCost,
    scheduleData,
    paymentMethod
  ) => {
    const { appData, cycleLength, property } = this.props;
    const {
      week,
      day,
      additionalService,
      serviceType,
      timeframe
    } = scheduleItem;
    const { cost: totalCost, discountDetails, couponDetails } = itemCost || {};

    if (!serviceType) {
      return null;
    }

    const { serviceDate, rescheduleItem } = scheduleData;

    if (!serviceDate) {
      return null;
    }
    let { momentDate } = serviceDate;

    if (rescheduleItem) {
      const { rescheduleDate, skip } = rescheduleItem || {};
      if (skip) {
        momentDate = momentDate.clone().add(cycleLength, 'week');
      } else {
        momentDate = rescheduleDate.momentDate.clone();
      }
    }

    return (
      <ShadowBox mb={4} key={`${serviceType}_${week}_${day}`}>
        {renderServiceItem(
          appData,
          property,
          serviceType,
          additionalService,
          momentDate,
          totalCost,
          discountDetails,
          couponDetails,
          paymentMethod,
          timeframe
        )}
      </ShadowBox>
    );
  };

  renderScheduledPayments = formValues => {
    const { scheduledItems, appData, selectedItems, property } = this.props;
    const { costs } = this.state;

    const { paymentMethod } = formValues || {};
    const fullAddress = transfromPropertyToAddress(property);

    return (
      <Box display="flex" flexDirection="column" alignItems="stretch">
        <Text fontSize={1} ml={3} mb={3}>
          Your Service
        </Text>
        <Text fontSize={1} ml={3} mb={3}>
          {fullAddress}
        </Text>
        {scheduledItems.map((item, index) => (
          <ScheduledCheckoutItem
            key={index}
            data={item}
            refData={appData}
            scheduleData={selectedItems[index]}
            costData={costs}
            renderDetail={this.renderScheduledItem}
            paymentMethod={paymentMethod}
          />
        ))}
      </Box>
    );
  };

  handleSubmit = async (values, actions) => {
    const { setSubmitting, setStatus } = actions;
    const {
      stripe,
      user,
      cycleLength,
      currentPackage,
      hasPaymentMethod,
      scheduledItems,
      startingDate,
      refreshUser,
      loadMyCleansList,
      recipient,
      isPMUser,
      onSubmit,
      goToReschedule,
      property
    } = this.props;

    setSubmitting(true);

    const { costs } = this.state;
    const { paymentMethod, couponInfo } = values;
    const { coupon } = couponInfo || {};
    const { propertyID } = property;

    let sourceId = '';
    if (!hasPaymentMethod && paymentMethod !== 'invoice') {
      try {
        const { source } = await stripe.createSource({
          type: 'card',
          owner: {
            name: `${user.firstName} ${user.lastName}`
          }
        });

        sourceId = source.id;
      } catch (err) {
        logger.log(err.message);
        return;
      }
    }

    if (sourceId) {
      try {
        await API.addPayment({
          token: StripeConfig.isProduction ? sourceId : 'tok_visa',
          isApplePay: false,
          isGooglePay: false
        });
      } catch (err) {
        const error = getErrors(err);
        if (error.message) {
          setStatus(error);
        } else {
          setStatus({
            message:
              Object.values(error).pop() ||
              'There was an error in updating payment info'
          });
        }
        setSubmitting(false);
        return;
      }
    }

    try {
      const { result: bookingResult } = await API.createRecurringBooking({
        package: currentPackage,
        coupon: coupon ? coupon : undefined,
        cycle: scheduledItems,
        startingDate: moment(startingDate).format('YYYY-MM-DD'),
        cycleLength,
        isPMUser,
        recipient,
        paymentMethod,
        propertyID
      });

      if (!hasPaymentMethod) {
        refreshUser();
      }
      loadMyCleansList();
      trackRecurSrvsBooked(coupon, bookingResult, scheduledItems, costs);
      loadPixel(); // tracking for submit-button conversion
      onSubmit();
    } catch (err) {
      if (!hasPaymentMethod) {
        refreshUser();
      }

      const error = getErrors(err);
      const { name, metaInfo } = error || {};

      if (checkIfRescheduleError(name)) {
        goToReschedule(metaInfo);
      }

      if (error.message) {
        setStatus(error);
      } else {
        setStatus({
          message:
            Object.values(error).pop() ||
            'There was an error in creating service'
        });
      }

      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  render() {
    const { hasPaymentMethod, isPMUser, onCancel } = this.props;
    const { isCalculating, costPayload } = this.state;

    if (isCalculating) {
      return (
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="Center"
        >
          <DefaultLoader size={64} />
        </Box>
      );
    }

    const initialValues = {
      isPaymentNeeded: !hasPaymentMethod,
      cardNumValid: false,
      cardExpValid: false,
      cardCVCValid: false,
      paymentMethod: 'credit'
    };

    if (!isPMUser) {
      initialValues.couponInfo = {
        coupon: '',
        isCouponValid: false
      };
    }

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={this.handleSubmit}
        validationSchema={validationSchema}
        validationOnChange
        initialErrors={validationSchema.isValidSync(initialValues)}
      >
        {({ isValid, isSubmitting, status, values, setFieldValue }) => {
          return (
            <Form>
              <Box display="flex" flexDirection="column">
                <Text fontSize={1} ml={3} mb={3}>
                  Payment Method
                </Text>
                <PaymentSelection
                  selectedMethod={values.paymentMethod}
                  mb={4}
                  onChange={method => {
                    setFieldValue('paymentMethod', method);
                    if (method === 'invoice') {
                      setFieldValue('isPaymentNeeded', false);
                    } else {
                      setFieldValue('isPaymentNeeded', !hasPaymentMethod);
                    }
                  }}
                />
                {!isPMUser && (
                  <>
                    <Text fontSize={1} ml={3} mb={3}>
                      Coupons
                    </Text>
                    <Field
                      component={CouponField}
                      onCostUpdate={this.handleCostUpdate}
                      mb={4}
                      name="couponInfo"
                      label="Coupon Code"
                      costPayload={costPayload}
                      isRecurring
                    />
                  </>
                )}
              </Box>

              {this.renderScheduledPayments(values)}

              {status && status.message && (
                <Text color="red" mb={4}>
                  {status.message}
                </Text>
              )}

              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={4}
              >
                <Button
                  onClick={onCancel}
                  type="button"
                  variant="secondary"
                  mr={4}
                  p={3}
                  flex={1}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  p={3}
                  flex={1}
                  disabled={!isValid || isSubmitting}
                >
                  Confirm
                </Button>
              </Box>
              <Text fontSize={0} color="grey.0" mb={4}>
                {SURCHARGE_TEXT}
              </Text>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

RecurringCheckoutForm.propTypes = {
  hasPaymentMethod: PropTypes.bool,
  stripe: PropTypes.any,
  appData: PropTypes.object,
  user: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  refreshUser: PropTypes.func,
  loadMyCleansList: PropTypes.func,
  goToReschedule: PropTypes.func,
  currentPackage: PropTypes.string,
  scheduledItems: PropTypes.array,
  selectedItems: PropTypes.array,
  startingDate: PropTypes.any,
  cycleLength: PropTypes.number,
  isPMUser: PropTypes.bool,
  recipient: PropTypes.any,
  property: PropTypes.object
};

const mapStatesToProps = state => ({
  appData: AppSelectors.selectAppData(state),
  user: AuthSelectors.selectCurrentUser(state),
  hasPaymentMethod: AuthSelectors.selectHasPaymentMethod(state),
  cycleLength: ScheduleSelectors.selectNumOfRows(state),
  currentPackage: ScheduleSelectors.selectPackage(state),
  scheduledItems: ScheduleSelectors.selectScheduledItems(state).map(
    transformSchedule
  ),
  selectedItems: ScheduleSelectors.selectScheduledItems(state),
  startingDate: ScheduleSelectors.selectFirstServiceDate(state),
  isPMUser: AuthSelectors.selectIsPMUser(state),
  recipient: BookingSelectors.selectRecipientInfo(state),
  property: BookingSelectors.selectCurrentProperty(state)
});

const mapDispatchToProps = dispatch => ({
  refreshUser: () => dispatch(AuthActions.setUserLoadRequest()),
  loadMyCleansList: () => dispatch(CleansActions.setMyCleansLoadRequest()),
  goToReschedule: info => {
    dispatch(ScheduleActions.setRecurringRescheduleInfo(info));
    dispatch(BookingActions.setBookingStep(BOOKING_STAGE.ADJUST));
  }
});

const connectedForm = connect(
  mapStatesToProps,
  mapDispatchToProps
)(RecurringCheckoutForm);

export default injectStripe(connectedForm);
